import React, { useEffect, useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import AdminPage from '../Components/AdminPage';
import FormSection from '../Components/FormSection';
import FileDrop from '../Components/FileDrop';
import AdminActionBar from '../Components/AdminActionBar';
import DangerZone from '../Components/DangerZone';
import ConfirmDialog from '../Components/ConfirmDialog';
import ExistingScreenshots from '../Components/ExistingScreenshots';
import GradientCircularProgress from '../Components/GradientCircularProgress';

const Update = (props) => {
  let compTemp = props.comps['driverApps'];
  let compTemp2 = compTemp.concat(props.comps['backSeatApps']);
  let compsTemp = compTemp2.concat(props.comps['bookingApps']);
  let compsNew = [];
  let ids = {};

  compsTemp.forEach((obj) => {
    if (!ids[obj.name]) {
      ids[obj.name] = true;
      compsNew.push(obj);
    }
  });

  const [affiliate, setAffiliate] = useState('');
  const [affiliateID, setAffiliateID] = useState(0);
  const [versionInfo, setVersionInfo] = useState('');
  const [aboutApp, setAboutApp] = useState('');
  const [appleLink, setAppleLink] = useState('');
  const [googleLink, setGoogleLink] = useState('');
  const [app, setApp] = useState('');
  const [appType, setAppType] = useState('');
  const [apps, setApps] = useState([]);

  const [id, setId] = useState(0);
  const [bgPhoto, setBgPhoto] = useState(null);
  const [appLogo, setAppLogo] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [screenshotList, setScreenshotList] = useState([]);

  const [updateComplete, setUpdateComplete] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /** Which destructive action is awaiting confirmation: 'record' | 'upload'. */
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (sessionStorage.getItem('app')) {
      const x = JSON.parse(sessionStorage.getItem('app'));
      setApp(x);
      setApps([x]);
      setAppType(x.appType);
      setAffiliate(x.name);
      setAffiliateID(x.iAffiliateID);
      setId(x.key);
      setScreenshotList(x.screenshots);
      setVersionInfo(x.versionInfo);
      setAboutApp(x.aboutApp);
      setAppleLink(x.appleLink);
      setGoogleLink(x.googleLink);
      /*
       * NOTE: these four are stored exactly as the payload gives them, nulls
       * included. React warns "`value` prop on `input` should not be null", so
       * every JSX site below renders `value={x ?? ''}` — a DISPLAY-only
       * coercion. Do NOT coerce here in state, and do NOT coerce at the fetch:
       * URLSearchParams stringifies null to the literal text "null", which is
       * what the API receives today, and Update.requests.test.js pins it.
       * Changing the state to '' would silently change what gets stored.
       */
    }
  }, []);

  function handleAffiliateChange(event) {
    setApp('');
    setAppType('');
    setAffiliate(event);
    setAffiliateID(event.iAffiliateID);

    let typeList = [];
    compsTemp.forEach((app) => {
      if (app.name === event) {
        typeList.push(app);
      }
    });
    setAffiliateID(typeList[0].iAffiliateID);
    setApps(typeList);
  }

  function handleAppChange(event) {
    setApp('');
    let currApp = '';
    apps.forEach((newApp) => {
      if (newApp.appType === event) {
        currApp = newApp;
      }
    });
    setApp(currApp);
    setAppType(currApp.appType);
    sessionStorage.setItem('app', JSON.stringify(currApp));
    setId(currApp.key);
    setScreenshotList(currApp.screenshots);
    setVersionInfo(currApp.versionInfo);
    setAboutApp(currApp.aboutApp);
    setAppleLink(currApp.appleLink);
    setGoogleLink(currApp.googleLink);
  }

  /*
   * The three request functions below are carried over from the pre-redesign
   * page UNCHANGED — including the duplicated URLSearchParams blocks and the
   * two full branches in handleUpdate.
   *
   * This is a deliberate instruction from the user, not an oversight, and it
   * overrides the usual DRY rule: /Update's whole purpose is correcting a bad
   * upload in place rather than deleting and re-uploading it, and that
   * behaviour must be preserved verbatim. Do not extract a shared params
   * helper, do not collapse the two branches into one fetch with a conditional
   * init, and do not reorder the keys. Update.requests.test.js pins the wire
   * format; this comment pins the shape.
   *
   * The one permitted difference: handleDeleteRecord and handleDeleteUpload no
   * longer take an event and no longer call preventDefault, because they are
   * now invoked from ConfirmDialog's onConfirm rather than a button's onClick.
   * Neither call ever depended on the event for anything else.
   */
  async function handleUpdate(e){
      const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
      const today = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
      e.preventDefault();
      const APILinksFetch = await fetch('/APILinks.json')
      const APILinks = await APILinksFetch.json()

      let data = new FormData();
      if(bgPhoto != null){
          data.append('BGPhoto', bgPhoto)
          app.BGPhoto = (`/pages/appPages/${app.appType}/${affiliate}/BGPhoto.png`)
      }
      if(appLogo != null){
          data.append('appLogo', appLogo)
          app.appLogo = (`/pages/appPages/${app.appType}/${affiliate}/logo.png`)
      }

      if(screenshots.length !== 0) {
        for(const file of screenshots){
          data.append('screenshots', file)
        }
      }

      setIsLoading(true);
      if (bgPhoto === null && appLogo === null && screenshots.length === 0){
          const response = await fetch(APILinks['updateApp'] + new URLSearchParams({
              id: Number(id),
              iAffiliateID: (Number(affiliateID)),
              affiliateName: affiliate,
              appType: app.appType,
              versionNo: app.versionNo,
              versionInfo: versionInfo,
              releaseDate: today,
              navLink: app.navLink,
              downloadLink: app.downloadLink,
              BGPhoto: app.BGPhoto,
              appLogo: app.appLogo,
              isTesting: app.isTesting,
              aboutApp: aboutApp,
              appleLink: appleLink,
              googleLink: googleLink,
          }).toString(),
          {
          method: 'PUT'
          })
          const updateData = await response.json()
          if (updateData['message'] === 'Completed'){
              setUpdateComplete(true)
          }
          else{
              setUpdateErrorMessage(updateData['message'])
              setUpdateError(true)
          }
      }
      else{
          const response = await fetch(APILinks['updateApp'] + new URLSearchParams({
              id: Number(id),
              iAffiliateID: (Number(affiliateID)),
              affiliateName: affiliate,
              appType: app.appType,
              versionNo: app.versionNo,
              versionInfo: versionInfo,
              releaseDate: today,
              navLink: app.navLink,
              downloadLink: app.downloadLink,
              BGPhoto: app.BGPhoto,
              appLogo: app.appLogo,
              isTesting: app.isTesting,
              aboutApp: aboutApp,
              appleLink: appleLink,
              googleLink: googleLink,
          }).toString(),
          {
          method: 'PUT',
          body: data
          })
          const updateData = await response.json()
          if (updateData['message'] === 'Completed'){
              setUpdateComplete(true)
          }
          else{
              setUpdateErrorMessage(updateData['message'])
              setUpdateError(true)
          }
      }
      sessionStorage.setItem("app", "")
  }

  async function handleDeleteRecord(){
      const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
      const today = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
      setPendingAction(null);
      const APILinksFetch = await fetch('/APILinks.json')
      const APILinks = await APILinksFetch.json()
      const response = await fetch(APILinks['deleteRecord'] + new URLSearchParams({
          id: Number(id),
          iAffiliateID: (Number(affiliateID)),
          affiliateName: affiliate,
          appType: app.appType,
          versionNo: app.versionNo,
          versionInfo: versionInfo,
          releaseDate: today,
          navLink: app.navLink,
          downloadLink: app.downloadLink,
          BGPhoto: app.BGPhoto,
          appLogo: app.appLogo,
          isTesting: app.isTesting,
          aboutApp: aboutApp,
          appleLink: appleLink,
          googleLink: googleLink,
      }).toString(),
      {
      method: 'POST'
      })
      const updateData = await response.json()
      if (updateData['message'] === 'Deleted'){
          sessionStorage.setItem("app", "");
          window.location.reload(false);
      }
      else{
          setUpdateErrorMessage(updateData['message'])
          setUpdateError(true)
      }
      sessionStorage.setItem("app", "")
  }

  async function handleDeleteUpload(){
      const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
      const today = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
      setPendingAction(null);
      const APILinksFetch = await fetch('/APILinks.json')
      const APILinks = await APILinksFetch.json()
      const response = await fetch(APILinks['deleteUpload'] + new URLSearchParams({
          id: Number(id),
          iAffiliateID: (Number(affiliateID)),
          affiliateName: affiliate,
          appType: app.appType,
          versionNo: app.versionNo,
          versionInfo: versionInfo,
          releaseDate: today,
          navLink: app.navLink,
          downloadLink: app.downloadLink,
          BGPhoto: app.BGPhoto,
          appLogo: app.appLogo,
          isTesting: app.isTesting,
          aboutApp: aboutApp,
          appleLink: appleLink,
          googleLink: googleLink,
      }).toString(),
      {
      method: 'POST'
      })
      const updateData = await response.json()
      if (updateData['message'] === 'Deleted'){
          sessionStorage.setItem("app", "");
          window.location.reload(false);
      }
      else{
          setUpdateErrorMessage(updateData['message'])
          setUpdateError(true)
      }
      sessionStorage.setItem("app", "")
  }

  function handleUpdateClose() {
    setUpdateComplete(false);
    setIsLoading(false);
  }

  function handleErrorClose() {
    setUpdateError(false);
    setIsLoading(false);
  }

  if (updateComplete) {
    return (
      <AdminPage title="Update an existing upload">
        <div className="itcFormBanner itcFormBannerSuccess" role="status">
          <p className="itcFormBannerText">Update completed</p>
          <button
            type="button"
            className="itcFormBannerDismiss"
            onClick={handleUpdateClose}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </AdminPage>
    );
  }

  let status = null;
  if (updateError) {
    status = (
      <div className="itcFormBanner itcFormBannerError" role="alert">
        <p className="itcFormBannerText">{updateErrorMessage}</p>
        <button
          type="button"
          className="itcFormBannerDismiss"
          onClick={handleErrorClose}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  } else if (isLoading) {
    status = <GradientCircularProgress />;
  }

  const title = app ? `${affiliate} · ${app.appType}` : 'Update an existing upload';

  // Guarded, because the copy below reads off `app` — which is the empty string
  // until an app is picked, and would render "Removes vundefined".
  const dangerActions = !app ? [] : [
    {
      id: 'upload',
      title: 'Roll back the latest version',
      // The endpoint behind this selects by affiliate + release date, not by
      // app type, so it is not guaranteed to remove a row of the app type
      // shown here — the copy stays deliberately non-committal about that.
      description: 'Removes the most recently released version for this affiliate, and its APK.',
      buttonLabel: 'Roll back',
      onSelect: () => setPendingAction('upload'),
    },
    {
      id: 'record',
      title: 'Delete this app entirely',
      description:
        'Removes all versions, artwork and screenshots. Cannot be undone.',
      buttonLabel: 'Delete app',
      onSelect: () => setPendingAction('record'),
    },
  ];

  return (
    <AdminPage title={title}>
      <form autoComplete="off" onSubmit={handleUpdate}>
        <div className="itcFormCard">
          <FormSection index={1} title="App" description="Which listing you are editing.">
            <TextField
              className="itcFieldHalf"
              name="affiliate"
              select
              required
              /*
               * Both required selects render their label as a <span>. MUI
               * otherwise folds a required-asterisk into the label element,
               * which changes the field's accessible name from "App Type" to
               * "App Type *" and breaks an anchored getByLabelText. The
               * pre-redesign page carried this on both fields for the same
               * reason; keeping it on only one also left the two visually
               * inconsistent.
               */
              InputLabelProps={{ component: 'span' }}
              value={affiliate}
              label="Affiliate"
              onChange={(event) => handleAffiliateChange(event.target.value)}
            >
              {compsNew.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            {affiliate && (
              <TextField
                className="itcFieldHalf"
                name="affiliateID"
                label="Affiliate ID"
                value={affiliateID}
                slotProps={{ input: { readOnly: true } }}
              />
            )}

            {affiliate && (
              <TextField
                className="itcFieldHalf"
                name="appType"
                select
                required
                InputLabelProps={{ component: 'span' }}
                value={appType}
                label="App Type"
                onChange={(event) => handleAppChange(event.target.value)}
              >
                {apps.map((option) => (
                  <MenuItem key={option.appType} value={option.appType}>
                    {option.appType}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FormSection>

          {app && (
            <FormSection index={2} title="Release" description="The version number is set at upload time.">
              <TextField
                className="itcFieldHalf"
                name="versionNo"
                label="Version #"
                value={app.versionNo}
                InputLabelProps={{ shrink: true }}
                slotProps={{ input: { readOnly: true } }}
              />

              <TextField
                className="itcFieldFull"
                name="versionInfo"
                label="Version Info"
                fullWidth
                multiline
                minRows={3}
                value={versionInfo ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setVersionInfo(event.target.value)}
              />
            </FormSection>
          )}

          {app && (
            <FormSection
              index={3}
              title="Files"
              description="Replacing artwork overwrites what is live. Removing a published screenshot takes effect immediately."
            >
              <div className="itcFieldHalf">
                <FileDrop
                  label="Background Photo"
                  accept="image/*"
                  value={bgPhoto}
                  onChange={setBgPhoto}
                  hint="Replaces the current one"
                />
              </div>

              <div className="itcFieldHalf">
                <FileDrop
                  label="App Logo"
                  accept="image/*"
                  value={appLogo}
                  onChange={setAppLogo}
                  hint="Replaces the current one"
                />
              </div>

              <div className="itcFieldFull">
                <p className="itcFieldSubLabel">Published screenshots</p>
                <ExistingScreenshots
                  screenshotList={screenshotList}
                  setComps={props.setComps}
                  app={app}
                />
              </div>

              <div className="itcFieldFull">
                <FileDrop
                  label="Screenshots"
                  accept="image/*"
                  multiple
                  value={screenshots}
                  onChange={setScreenshots}
                  hint="Added alongside the published ones"
                />
              </div>
            </FormSection>
          )}

          {app && (
            <FormSection index={4} title="Store listing" description="What visitors read on the app's page.">
              <TextField
                className="itcFieldFull"
                name="aboutApp"
                label="About this app"
                fullWidth
                multiline
                minRows={3}
                value={aboutApp ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setAboutApp(event.target.value)}
              />

              <TextField
                className="itcFieldHalf"
                name="appleLink"
                label="Apple Store Link"
                value={appleLink ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setAppleLink(event.target.value)}
              />

              <TextField
                className="itcFieldHalf"
                name="googleLink"
                label="Play Store Link"
                value={googleLink ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setGoogleLink(event.target.value)}
              />
            </FormSection>
          )}
        </div>

        {app && (
          <AdminActionBar
            requirements={[
              { label: 'Affiliate', done: affiliate !== '' },
              { label: 'App Type', done: appType !== '' },
            ]}
            submitLabel="Update"
            status={status}
            busy={isLoading}
          />
        )}
      </form>

      {app && (
        <>
          <DangerZone actions={dangerActions} />

          <ConfirmDialog
            open={pendingAction === 'upload'}
            title={`Roll back ${affiliate} · ${app.appType} to the previous version?`}
            description={`This removes the most recently released version for this affiliate (currently v${app.versionNo}) and its APK from the store.`}
            confirmWord={affiliate}
            confirmLabel="Roll back"
            onConfirm={handleDeleteUpload}
            onClose={() => setPendingAction(null)}
          />

          <ConfirmDialog
            open={pendingAction === 'record'}
            title={`Delete ${affiliate} · ${app.appType}?`}
            description="This removes every version, the artwork and all screenshots for this app. It cannot be undone."
            confirmWord={affiliate}
            confirmLabel="Delete app"
            onConfirm={handleDeleteRecord}
            onClose={() => setPendingAction(null)}
          />
        </>
      )}
    </AdminPage>
  );
};

export default Update;

Update.propTypes = {
  comps: PropTypes.any,
  setComps: PropTypes.func,
};
